// Styles
import "../chat-item/chat-item.scss";
import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { useDispatch, useSelector } from "react-redux";
// Components
import { CompactTextInput } from "../../../../../../../../../../shared/components/inputs/compact-text-input/CompactTextInput";
import { DeleteConfirmModal } from "../delete-confirm-modal/DeleteConfirmModal";
// Actions
import {
	setActiveDraftId,
	setIsLoading,
	setSessionData,
} from "../../../../../../../../../../shared/store/slices/session.slice";
// Types
import { RootState } from "../../../../../../../../../../shared/store/store";
import type { DraftSession } from "../../../dialog/utils/draft-sessions-storage";
import {
	removeDraft,
	setDraftCustomTitle,
} from "../../../dialog/utils/draft-sessions-storage";
// Hooks
import { useRelativeTime } from "../../../../../../../../../../shared/hooks/useRelativeTime";

interface Props {
	draft: DraftSession;
	updateDate: string;
	isActive: boolean;
}

export const ChatItemDraft = ({ draft, updateDate, isActive }: Props) => {
	const dispatch = useDispatch();
	const chatItemRef = useRef<HTMLDivElement | null>(null);
	const optionsWrapperRef = useRef<HTMLDivElement | null>(null);
	const inputWrapperRef = useRef<HTMLDivElement | null>(null);
	const [isOptionsVisible, setIsOptionsVisible] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editTitle, setEditTitle] = useState(draft.title);
	const [itemTitle, setItemTitle] = useState(draft.title);
	const relativeDraftTime = useRelativeTime(draft.updated_at);
	const globalSessionId = useSelector(
		(state: RootState) => state.session.sessionId,
	);
	const globalActiveDraftId = useSelector(
		(state: RootState) => state.session.activeDraftId,
	);

	useEffect(() => {
		setItemTitle(draft.title);
		setEditTitle(draft.title);
	}, [draft.title, draft.draft_id]);

	const onHandleDelete = () => {
		removeDraft(draft.draft_id);
		if (globalActiveDraftId === draft.draft_id) {
			dispatch(setActiveDraftId(null));
			dispatch(setSessionData(null));
			dispatch(setIsLoading(false));
		}
		gsap.to(chatItemRef.current, {
			height: 0,
			duration: 0.2,
			ease: "power1.out",
		});
	};

	const onSaveCustomTitle = () => {
		setDraftCustomTitle(draft.draft_id, editTitle);
		setIsEditMode(false);
		setItemTitle(editTitle.trim() || draft.title);
	};

	const onTitleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== "Enter" || !isEditMode) return;
		event.preventDefault();
		onSaveCustomTitle();
	};

	const onHandleOptionsClick = () => {
		if (!isOptionsVisible) {
			setIsOptionsVisible(true);
			gsap.fromTo(
				optionsWrapperRef.current,
				{ opacity: 0, x: 50 },
				{
					opacity: 1,
					x: 0,
					duration: 0.3,
					ease: "elastic.out(1.4, 1.6)",
				},
			);
		} else {
			gsap.fromTo(
				optionsWrapperRef.current,
				{ opacity: 1, x: 0 },
				{
					opacity: 0,
					x: 50,
					duration: 0.15,
					ease: "power1.out",
					onComplete: () => setIsOptionsVisible(false),
				},
			);
		}
	};

	useEffect(() => {
		if (!isOptionsVisible) return;
		const handleDocumentClick = (e: MouseEvent) => {
			const target = e.target as Node | null;
			if (!optionsWrapperRef.current) return;
			const isInsideInput = inputWrapperRef.current?.contains(target) ?? false;
			const isInsideOptions = optionsWrapperRef.current.contains(target);
			if (target && !isInsideOptions && !isInsideInput) {
				gsap.fromTo(
					optionsWrapperRef.current,
					{ opacity: 1, x: 0 },
					{
						opacity: 0,
						x: 50,
						duration: 0.15,
						onComplete: () => {
							setIsOptionsVisible(false);
							if (isEditMode) setIsEditMode(false);
						},
					},
				);
			}
		};
		document.addEventListener("click", handleDocumentClick);
		return () => document.removeEventListener("click", handleDocumentClick);
	}, [isOptionsVisible, isEditMode]);

	return (
		<div
			ref={chatItemRef}
			onClick={() => {
				if (globalSessionId !== null) {
					dispatch(setSessionData(null));
				}
				if (globalActiveDraftId !== draft.draft_id) {
					dispatch(setActiveDraftId(draft.draft_id));
					dispatch(setIsLoading(false));
				}
			}}
			className={`chat-item chat-item--draft ${isActive ? "active" : ""}`}>
			<div className='chat-item__content'>
				<div className='chat-item__title'>
					{isEditMode ? (
						<div
							ref={inputWrapperRef}
							className='chat-item__title-input-wrapper'>
							<CompactTextInput
								value={editTitle}
								className='chat-item__title-input'
								actionInput={(value) => setEditTitle(value)}
								onKeyDown={onTitleInputKeyDown}
							/>
						</div>
					) : (
						<div>{itemTitle}</div>
					)}
				</div>
				<div className='chat-item__bottom'>
					<div className='chat-item__date'>{updateDate}</div>
					<div className='chat-item__draft-label'>Чернетка</div>
					{relativeDraftTime && (
						<span className='chat-item__draft-time'>
							({relativeDraftTime})
						</span>
					)}
				</div>
			</div>
			<div
				ref={optionsWrapperRef}
				className={`chat-item__menu ${isOptionsVisible ? "visible" : ""}`}>
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (isEditMode) {
							onSaveCustomTitle();
						} else {
							setIsEditMode(true);
						}
					}}
					className={`chat-item__menu-edit ${isEditMode ? "save" : ""}`}>
					{isEditMode ? (
						<img src='/img/icons/save.png' alt='save icon' />
					) : (
						<img src='/img/icons/edit.png' alt='edit icon' />
					)}
				</button>
				<button
					onClick={(e) => {
						e.stopPropagation();
						setIsDeleteModalOpen(true);
					}}
					className='chat-item__menu-delete'>
					<img src='/img/icons/delete.png' alt='delete icon' />
				</button>
			</div>
			<button
				onClick={(e) => {
					e.stopPropagation();
					onHandleOptionsClick();
				}}
				className='chat-item__options'>
				<img src='/img/icons/more.png' alt='more icon' />
			</button>
			<DeleteConfirmModal
				isOpen={isDeleteModalOpen}
				title={itemTitle}
				onCancel={() => setIsDeleteModalOpen(false)}
				onConfirm={() => {
					setIsDeleteModalOpen(false);
					onHandleDelete();
				}}
			/>
		</div>
	);
};
